/*
 * Copyright (C) 2014 Spanning Cloud Apps LLC.  All rights reserved.
 */

'use strict';
angular
  .module('http.bulkrestore', [])
  /**
   * Service used to access all calls to the bulk restore rest service.
   */
  .factory('httpBulkRestore', function ($http) {
    /**
     * Get the object types to restore for the organization.
     *
     * @returns {Promise} - A promise that when resolved will include an object.
     */
    var getTypes = function (params) {
      return $http({
        method: 'GET',
        url: 'api/restore/types',
        params
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Get the fields for object types.
     *
     * @param {String[]} objectTypes - The object types of the fields to get.
     * @returns {Promise} - A promise that when resolved will include an object.
     */
    const getFieldsForObjects = function (objectTypes) {
      return $http({
        method: 'GET',
        url: 'api/restore/fields',
        params: {
          objectTypes
        }
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Get object with list of records and optionally the total count for date range.
     * @param {Object} params - A parameters object
     * @returns {Promise} A promise that when resolved will include an object.
     */
    const searchRecordsPage = function (params) {
      return $http({
        method: 'GET',
        url: 'api/restore-search',
        params
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Get the record details.
     *
     * @param {Number} id - id of record.
     * @param {Number} batchId - batch id of record.
     *
     * @returns {Promise} - A promise that when resolved will include record details.
     */
    const getRecordPreview = function (id, batchId) {
      const params = {
        id: id,
        batchId
      };

      return $http({
        method: 'GET',
        url: 'api/restore/preview',
        params: params
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Get the record descendants.
     *
     * @param {Number} id - id of record to get descendants for.
     *
     * @returns {Promise} - A promise that when resolved will include record descendants.
     */
    const getRecordChildren = function (id, batchId) {
      const params = {
        id: id,
        batchId
      };

      return $http({
        method: 'GET',
        url: 'api/restore/deleted/descendants',
        params: params
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Get the bulk restore details.
     * @param {Object} params - A parameters object
     * @param {Number} params.batchId - The batch id to search against.
     * @param {Number} [param.page] - The current selected page.
     * @param {Number} [param.pageSize] - The number of records per page.
     * @param {String} [params.filter] -  The filter string to apply.
     * @param {String} [params.sort='sfName'] -  Property to sort by.
     * @param {Boolean} [params.ascending=true] - True to sort in ascending order.
     * @returns {Promise} - A promise that when resolved will include an object.
     */
    var getDetails = function (params) {
      return $http({
        method: 'GET',
        url: 'api/restore/details/successful',
        params: params
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Get the csv link to download the batch details for the bulk restore event.
     * @param {Object} params - A parameters object
     * @param {Number} params.batchId - The batch id to search against.
     * @param {String} params.targetSfOrgId - The target sf org id for a bulk restore.
     * @returns {Promise} - A promise that when resolved will include an object.
     */
    var getCsv = function (params) {
      return $http({
        method: 'GET',
        url: 'api/restore/csv',
        params: params
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Returns a list of the distinct object types for a batch.
     *
     * @param {Number} batchId - The CSV verification batch id.
     * @returns {Promise} A promise resolving to the array of object types.
     */
    function getCsvObjectTypes(batchId) {
      return $http({
        method: 'GET',
        url: 'api/restore/csv-object-types',
        params: { batchId: batchId }
      }).then(function (res) {
        return res.data;
      });
    }

    /**
     * Get the pre signed url for file download from s3.
     *
     * @param {Number} batchId - The id of the bulk restore batch whose csv is to be downloaded.
     * @returns {Promise<String>} - A promise that resolves to the pre-signed URL.
     */
    var getPreSignedCsvDownloadUrl = function (batchId) {
      return $http({
        method: 'GET',
        url: 'api/restore/download-csv-url',
        params: { batchId: batchId }
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Get pre signed post request.
     *
     * @param {Number} uploadTimestamp - The timestamp to mark the s3 key with.
     * @returns {Promise<String>} A promise that when resolved will include an object.
     */
    var getPreSignedPostUrl = function (uploadTimestamp) {
      return $http({
        method: 'GET',
        url: 'api/restore/upload-csv-url',
        params: { uploadTimestamp: uploadTimestamp }
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Get the verified records by batch id to restore.
     *
     * @param {Object} params - A parameters object
     * @param {String} params.type - The object type.
     * @param {Number} params.batchId - The batch id to search against.
     * @param {Number} [param.page] - The current selected page.
     * @param {Number} [param.pageSize] - The number of records per page.
     * @param {String} [params.sort='lastModified'] -  Property to sort by.
     * @param {Boolean} [params.ascending=true] - True to sort in ascending order.
     * @returns {Promise} - A promise that when resolved will include an object.
     */
    var getVerifiedRecordsByBatchId = function (params) {
      return $http({
        method: 'GET',
        url: 'api/restore/verifiedbatch',
        params: params
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Function that will get the presets for an org
     *
     * @returns {Promise} - A promise for the list of presets with their values
     */
    var getPresets = function () {
      return $http({
        method: 'GET',
        url: 'api/overwrite-templates'
      }).then(function (res) {
        return res.data;
      });
    };

    var deletePreset = function (presetId) {
      return $http({
        method: 'DELETE',
        url: 'api/overwrite-templates/' + presetId
      });
    };

    var savePreset = function (preset) {
      return $http({
        method: 'POST',
        url: 'api/overwrite-templates',
        data: preset
      });
    };

    /**
     * Function used to generate and download csv.
     *
     * @param {Object} params - A parameters object
     * @param {Number} params.batchId - The batch id of items to get for the csv.
     *
     * @returns {Promise} - A promise that resolves to an object.
     */
    var getInvalidIdCsv = function (params) {
      return $http({
        method: 'GET',
        url: 'api/restore/invalid-id-csv',
        params: params
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Upload the file to s3 using pre signed url.
     *
     * @param {String} url - The url to post to
     * @param {File} file - The file to upload.
     * @returns {Promise} - A promise that when resolved will include an object indicating a success.
     */
    var uploadFileToUrl = function (url, file) {
      return $http({
        method: 'PUT',
        headers: { 'Content-Type': 'text/csv' },
        url: url,
        data: file
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Save uploaded to the S3 file info in the db
     *
     * @param { Number } timestamp - The timestamp of uploaded file.
     * @param { String } fileName - The uploaded original file name.
     * @param { String } jobType - The job type we uploaded the file for.
     * @returns {Promise}
     */
    const saveUploadedFileInfo = function (timestamp, fileName, jobType) {
      return $http({
        method: 'POST',
        url: 'api/load-csvs',
        data: {
          timestamp,
          fileName,
          jobType
        }
      }).then(function (res) {
        return res.data;
      });
    };

    return {
      deletePreset: deletePreset,
      getPresets: getPresets,
      getTypes: getTypes,
      searchRecordsPage,
      getCsv: getCsv,
      getCsvObjectTypes: getCsvObjectTypes,
      getDetails: getDetails,
      getFieldsForObjects,
      getInvalidIdCsv: getInvalidIdCsv,
      getRecordPreview: getRecordPreview,
      getRecordChildren: getRecordChildren,
      getPreSignedCsvDownloadUrl: getPreSignedCsvDownloadUrl,
      getPreSignedPostUrl: getPreSignedPostUrl,
      getVerifiedRecordsByBatchId: getVerifiedRecordsByBatchId,
      savePreset: savePreset,
      uploadFileToUrl: uploadFileToUrl,
      saveUploadedFileInfo
    };
  });
